import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { BatchRequestItem } from '../models/batch-request-item.model';
import { HttpRequestMethod } from '../models/http-request-method.enum';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenService } from './access-token.service';
import { GraphService } from './graph.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';

export type PictureSize = '48x48' | undefined;

/**
 * Service that makes requests to Graph apis to get profile pictures.
 */
@Injectable({
    providedIn: 'root'
})
export class GraphPictureService extends GraphService {
    private readonly batchEnabled: boolean;
    private readonly cache: Map<string, Promise<string>> = new Map();

    constructor(
        capabilitiesService: CapabilitiesService,
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, contextService, officeHomeApiConfigService, platformLocation, globalErrorHandler, scheduler);

        this.batchEnabled = capabilitiesService.capabilities.batchGraphEnabled;
    }

    public getPeoplePictures(pictureIds: Array<string>): Promise<Array<string>> {
        if (this.batchEnabled) {
            return new Promise<Array<string>>((resolve, reject) => {
                const peoplePicturesRequest: Array<BatchRequestItem<string>> = pictureIds.map(pictureId => {
                    const url = `users/${pictureId}/photo/$value`;
                    return {
                        eventName: KnownInstrumentationEvents.OneDriveGraphPeoplePictureRequest,
                        endpoint: url,
                        method: HttpRequestMethod.Get,
                        cacheResult: true
                    };
                });
                this.batchGraph(
                    peoplePicturesRequest,
                    KnownInstrumentationEvents.OneDriveGraphBatchPeoplePicturesRequest)
                    .then(responses => {
                        resolve((responses || []).map(response => response && response.ok
                            ? response.data as string
                            : null));
                    },
                    reject);
            });
        } else {
            const promiseArray: Array<Promise<string>> = [];
            for (let i = 0; i < pictureIds.length; i++) {
                promiseArray.push(this.getPeoplePicture(pictureIds[i]));
            }
            return Promise.all(promiseArray).then(images => [].concat(...images));
        }
    }

    public getPeoplePicture(pictureId: string, size: PictureSize = undefined): Promise<string> {
        if (this.cache.has(pictureId)) {
            return this.cache.get(pictureId);
        }

        const apiPath = size === undefined ? `users/${pictureId}/photo/$value` : `users/${pictureId}/photos/${size}/$value`;
        const reader = new FileReader();
        const getPicture = new Promise<string>(resolve => {
            this.getGraph<Blob>(
                KnownInstrumentationEvents.OneDriveGraphPeoplePictureRequest,
                apiPath,
                FetchStrategy.NetworkOnly,
                null,
                true,
                null,
                null,
                null,
                this.batchEnabled,
                HttpResponseContentType.Blob)
                .then(response => {
                    if (response && response.ok) {
                        reader.onloadend = () => {
                            const result = (reader.result && typeof reader.result === 'string' && reader.result.split(',')) || [];
                            if (result.length > 1) {
                                resolve(result[1]);
                            } else {
                                resolve(null);
                            }
                        };
                        reader.onerror = () => resolve(null);
                        reader.onabort = () => resolve(null);
                        reader.readAsDataURL(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.H5HgW);

                    resolve(null);
                });
        });

        this.cache.set(pictureId, getPicture);

        return getPicture;
    }
}
